/** Analytics guest CC — always appended to Cal.com bookings. */
export const CAL_BOOKING_GUEST = 'matchmaking@roziesynopsis.com';

/** Prose before the Match token. Backend webhook regex extracts UUID from
 *  notes containing the literal prefix `Match:` — do not rename or reformat. */
export const CAL_BOOKING_NOTES_PREFIX =
  'This meeting is booked by Rozie Synopsis. Do not remove the guest matchmaking@roziesynopsis.com or the Match id below as they are used for analytics.';

export interface BookingAttendee {
  name?: string | null;
  email?: string | null;
}

export interface CalBookingContext {
  attendee: BookingAttendee;
  matchId?: string | null;
}

/** True when exhibitor metadata qualifies for Cal.com iframe booking. */
export function isCalComBookingEligible(
  meetingIntegration: string | null | undefined,
  meetingLink: string | null | undefined
): boolean {
  if (meetingIntegration !== 'cal.com') return false;
  const link = meetingLink?.trim();
  if (!link) return false;
  try {
    const { protocol } = new URL(link);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/** Build Cal.com deep-link with attendee prefill, analytics guest, and Match token. */
export function buildCalComBookingUrl(
  meetingLink: string,
  { attendee, matchId }: CalBookingContext
): string {
  const u = new URL(meetingLink.trim());
  const params = u.searchParams;

  const name = attendee.name?.trim();
  const email = attendee.email?.trim();
  if (name) params.set('name', name);
  if (email) params.set('email', email);
  params.set('guests', CAL_BOOKING_GUEST);

  const id = matchId?.trim();
  let notes = CAL_BOOKING_NOTES_PREFIX;
  if (id) {
    notes += ` Match:${id}`;
  } else {
    console.warn(
      '[booking] Cal.com URL built without matchId — webhook correlation unavailable'
    );
  }
  params.set('notes', notes);

  return u.toString();
}

/** cal.com inline embed: appending `?embed=true` renders just the booking
 *  widget (no marketing header / sign-in chrome). Non-cal.com links pass through. */
export function toEmbedUrl(link: string): string {
  try {
    const u = new URL(link);
    if (/(^|\.)cal\.com$/i.test(u.hostname) && !u.searchParams.has('embed')) {
      u.searchParams.set('embed', 'true');
    }
    return u.toString();
  } catch {
    return link;
  }
}

export function buildCalComEmbedBookingUrl(
  meetingLink: string,
  ctx: CalBookingContext
): string {
  return toEmbedUrl(buildCalComBookingUrl(meetingLink, ctx));
}
